/**
 * LatestPulseLine — the one-line "freshest update" beat that sits in
 * the hero footer band alongside the pinned-links chip strip.
 *
 * Status Pulse is being used as an ongoing running log of updates
 * (newest entry on top, history below). The full log doesn't deserve
 * the prime on-page real estate it used to occupy — so it gets
 * collapsed to its freshest line right here, with the rest of the
 * history living in a separate surface (drawer, when that ships).
 *
 * Until the drawer exists, `onSeeAll` can scroll to the existing
 * in-body Status Pulse card.
 */
import { cn } from "@/lib/cn";

interface Props {
  /** Raw status-update text. The component takes the first non-empty
   *  line as "the freshest entry" — matches the team's convention of
   *  putting newest-on-top, e.g. "5/20: ...\n5/19: ...". */
  text: string | null | undefined;
  /** "View all" action. Scroll to / open the full history surface. */
  onSeeAll: () => void;
  className?: string;
}

export function LatestPulseLine({ text, onSeeAll, className }: Props) {
  const cleaned = (text ?? "").trim();
  // Empty state — render a CTA that opens the drawer so a brand-new
  // project still surfaces Status Pulse as an entry point. Before
  // this, an empty status_update meant the line vanished entirely
  // AND the dedicated nav button was retired with the drawer move,
  // so there was no way to add the first pulse without poking the
  // URL. ETE-83 was the surface of that gap.
  if (!cleaned) {
    return (
      <button
        type="button"
        onClick={onSeeAll}
        className={cn(
          "group inline-flex max-w-full items-baseline gap-2 text-left text-xs text-ink-muted hover:text-brand-navy-700 dark:hover:text-brand-navy-300 transition-colors",
          className,
        )}
      >
        <span className="shrink-0 text-[10px] font-bold uppercase tracking-wider text-ink-3">
          Status Pulse
        </span>
        <span className="min-w-0 flex-1 truncate italic">
          No update yet
        </span>
        <span className="shrink-0 text-[10px] font-medium text-brand-navy-600 dark:text-brand-navy-400 group-hover:text-brand-navy-800">
          + Add →
        </span>
      </button>
    );
  }
  const firstLine =
    cleaned.split(/\r?\n/).map((s) => s.trim()).find((s) => s.length > 0) ?? "";
  // Truncation cap — keeps the line within the hero footer band even
  // on a moderate viewport. Slightly generous so a typical
  // "5/20: <one short sentence>" fits without ellipsis.
  const MAX = 90;
  const display = firstLine.length > MAX ? `${firstLine.slice(0, MAX - 1).trimEnd()}…` : firstLine;
  return (
    <button
      type="button"
      onClick={onSeeAll}
      title={firstLine}
      className={cn(
        "group inline-flex max-w-full items-baseline gap-2 text-left text-xs text-ink-3 hover:text-ink transition-colors",
        className,
      )}
    >
      <span className="shrink-0 text-[10px] font-bold uppercase tracking-wider text-ink-3">
        Status Pulse
      </span>
      <span className="min-w-0 flex-1 truncate italic">{display}</span>
      <span className="shrink-0 text-[10px] font-medium text-brand-navy-600 dark:text-brand-navy-400 group-hover:text-brand-navy-800">
        View all →
      </span>
    </button>
  );
}
